import type {
  LiftComputed,
  LiftDetail,
  LiftGeometry,
  LiftHardware,
  LiftLoading,
  PlanDataV2,
} from '@/types/planData';

// ---------------------------------------------------------------------------
// planData codec — CLIENT side (blueprint v2 §2.4 / §7.4, task B-4).
// Server mirror: server/src/features/construction-plans/planData.codec.ts.
//
// readPlan(row): missing schemaVersion ⇒ v1 ⇒ upgraded IN-MEMORY to a v2 VIEW
// (read path). The AI-generated top-level shape {phases, equipmentList,
// crewRequirements, safetyConsiderations, estimatedDuration, aiGenerated}
// nests under aiPlan.*.
// upgradeV1toV2(v1): ADDITIVE ONLY — v1 liftSystems[i] → liftDetails[i] with
// scenario = heelBlock ? 'S2-top-heel' : 'S1-top-only'; existing form keys
// verbatim; sets migratedAt.
// writePlan(v2): payload builder for planApi create/update (planType +
// schemaVersion stamped; livingDocument present).
// ---------------------------------------------------------------------------

const STRUCTURE_TYPES = ['monopole', 'self-support', 'guyed', 'mast-pipe'] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function asNumber(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

/** AI-generated top-level shape (server AI services' planData output). */
export function isAiGeneratedShape(data: Record<string, unknown>): boolean {
  return data.schemaVersion === undefined && (data.aiGenerated === true || Array.isArray(data.phases));
}

/** Unsaved-form v1 shape (CreateConstructionPlanPage payload). */
export function isV1FormShape(data: Record<string, unknown>): boolean {
  return data.schemaVersion === undefined && !isAiGeneratedShape(data);
}

function emptyComputed(): LiftComputed {
  // computed is a CACHE (PRD §2.3): the engine re-derives it on every
  // render/load and NEVER trusts stored numbers. Upgraded rows carry an
  // empty cache + advisory; consumers must recompute.
  return {
    engineVersion: '2.0.0',
    derivedAngles: { loadPositionDeg: 0, tagAngleDeg: null, fleetAngleDeg: null, slingAlphaDeg: null },
    forces: {
      linePartTensionLbs: 0,
      topBlockResultantLbs: 0,
      heelBlockLbs: null,
      jumpBlockLbs: null,
      redirectBlockLbs: null,
      mountBlockLbs: null,
      tieBackLbs: null,
      tagLineLbs: null,
      staticLineTensionLbs: null,
      slingLegLbs: null,
      winchPullLbs: 0,
      highlineTensionLbs: null,
      structureReactions: { topLateralLbs: 0, topVerticalLbs: 0, baseMomentFtLbs: 0, perAttachment: [] },
    },
    tieBackCatchLbs: null,
    working: [],
    refusals: [],
    advisories: [
      {
        code: 'migrated-empty-cache',
        message:
          'Computed cache empty after v1→v2 upgrade — recomputed by the engine on load (never trusted from storage).',
      },
    ],
    status: 'ok',
  };
}

/** One v1 liftSystems[i] → LiftDetail (additive; scenario from heelBlock presence). */
function upgradeLiftSystem(
  lift: Record<string, unknown>,
  index: number,
  v1: Record<string, unknown>
): LiftDetail {
  const structureData = isRecord(v1.structureData) ? v1.structureData : {};
  const projectInfo = isRecord(v1.projectInfo) ? v1.projectInfo : {};

  const topBlockHeight = asNumber(lift.topBlockHeight);
  const heelBlockHeight = asNumber(lift.heelBlockHeight);
  const hasHeelBlock = heelBlockHeight > 0;

  const rawStructureType = asString(projectInfo.structureType);
  const structureType = (STRUCTURE_TYPES as readonly string[]).includes(rawStructureType)
    ? (rawStructureType as (typeof STRUCTURE_TYPES)[number])
    : 'guyed';

  const topBlockAttachmentMethod: LiftGeometry['topBlock']['attachment']['method'] =
    lift.topSheaveLocation === 'gin-pole' ? 'mastPipeRoosterHead' : 'legPanelPoint';

  const parts = asNumber(lift.loadPartConfiguration, 1);
  const blockParts = (parts >= 1 && parts <= 4 ? Math.round(parts) : 1) as 1 | 2 | 3 | 4;

  const geometry: LiftGeometry = {
    structureType,
    structureHeightFt: asNumber(structureData.height, topBlockHeight),
    faceWidthFt: asNumber(structureData.faceWidthAtBase, 0) || undefined,
    topBlock: {
      heightFt: topBlockHeight,
      offsetFromCenterFt: 0,
      attachment: { method: topBlockAttachmentMethod },
    },
    heelBlock: hasHeelBlock
      ? {
          heightFt: heelBlockHeight,
          offsetFromTowerFt: asNumber(lift.heelBlockDistance),
          attachment: { method: 'legPanelPoint' },
        }
      : null,
    jumpBlock: null,
    redirectBlock: null,
    mountBlock: null,
    tieBackAnchor: null,
    highline: null,
    highestAttachmentFt: Math.max(topBlockHeight, hasHeelBlock ? heelBlockHeight : 0),
    winch: {
      distanceFromTowerFt: asNumber(lift.hoistDistance),
      heightFt: 0,
      side: 'A',
      type: 'winch',
    },
    load: {
      heightFt: asNumber(lift.loadHeight),
      distanceFromTowerFt: asNumber(lift.loadDistance),
    },
    tag: {
      method: 'straight',
      anchorDistanceFt: asNumber(lift.tagDistance),
      anchorHeightFt: 0,
    },
  };

  const hardware: LiftHardware = {
    blockParts,
    blockAttachment: '1-leg',
    slingHitch: '',
    // Migration defaults where v1 carried no value — additive upgrade, never
    // invented measurements beyond documented defaults (frictionFactorPerSheave
    // 1.0 = D10 ideal default; dynamicFactor 1.0 = D11 default).
    ropeType: 'synthetic',
    ropeDiameterIn: 0,
    sheaveCount: 0,
    sheaveBearing: 'plain',
    frictionFactorPerSheave: 1.0,
    equipment: [],
  };

  const loading: LiftLoading = {
    grossLoadLbs: asNumber(lift.grossLoad),
    dynamicFactor: 1.0,
    accountsForFriction: lift.accountsForFriction === true,
  };

  return {
    id: `lift-${index + 1}`,
    name: asString(lift.liftName, `Lift ${index + 1}`),
    scenario: hasHeelBlock ? 'S2-top-heel' : 'S1-top-only',
    personnelLift: false,
    geometry,
    hardware,
    loading,
    computed: emptyComputed(),
    complianceRefs: [],
    signOff: { reviewedBy: null, at: null },
  };
}

/**
 * Additive v1 → v2 upgrade (D3/Z2: non-destructive; v1 keys kept verbatim,
 * v2 keys layered on). Handles BOTH known incompatible v1 shapes.
 */
export function upgradeV1toV2(v1: Record<string, unknown>, migratedAt?: string): PlanDataV2 {
  const at = migratedAt ?? new Date().toISOString();

  let aiPlan: PlanDataV2['aiPlan'] = null;
  if (isAiGeneratedShape(v1)) {
    aiPlan = {
      phases: Array.isArray(v1.phases) ? v1.phases : [],
      crewRequirements: v1.crewRequirements ?? null,
      safetyConsiderations: v1.safetyConsiderations ?? null,
      estimatedDuration: v1.estimatedDuration ?? null,
      aiGenerated: v1.aiGenerated === true,
      generatedAt: asString(v1.generatedAt, at),
    };
  }

  const rawLiftSystems = Array.isArray(v1.liftSystems) ? (v1.liftSystems as unknown[]) : [];
  // Audit #1 (CRITICAL): when the payload already carries serialized v2
  // liftDetails (the real editing surface), use them verbatim instead of
  // re-deriving from v1 liftSystems (which would regenerate S1/S2, DLF 1.0,
  // personnelLift false and null jump/redirect/mount/tieBack/highline).
  const serializedLiftDetails = Array.isArray(v1.liftDetails)
    ? (v1.liftDetails as unknown[]).filter((l) => isRecord(l))
    : [];
  const liftDetails: LiftDetail[] =
    serializedLiftDetails.length > 0
      ? (serializedLiftDetails as unknown as LiftDetail[])
      : rawLiftSystems
          .map((lift, index) => (isRecord(lift) ? upgradeLiftSystem(lift, index, v1) : null))
          .filter((lift): lift is LiftDetail => lift !== null);

  let equipmentLists: unknown[][] = [];
  if (Array.isArray(v1.equipmentLists)) {
    equipmentLists = (v1.equipmentLists as unknown[]).map((list) => (Array.isArray(list) ? list : []));
  } else if (Array.isArray(v1.equipmentList)) {
    equipmentLists = [v1.equipmentList as unknown[]];
  }

  return {
    // v1 keys kept VERBATIM (additive only — includes liftSystems when present)
    ...(v1 as object),
    schemaVersion: 2,
    planType: 'construction',
    classification: isRecord(v1.classification)
      ? (v1.classification as unknown as PlanDataV2['classification'])
      : null,
    projectInfo: isRecord(v1.projectInfo) ? v1.projectInfo : {},
    contacts: Array.isArray(v1.contacts) ? v1.contacts : [],
    structureData: isRecord(v1.structureData) ? v1.structureData : {},
    duration: isRecord(v1.duration) ? v1.duration : {},
    scopeOfWork: asString(v1.scopeOfWork),
    meansAndMethods: asString(v1.meansAndMethods),
    constructionSequencing: asString(v1.constructionSequencing),
    personnel: isRecord(v1.personnel)
      ? (v1.personnel as PlanDataV2['personnel'])
      : {
          qualifiedPerson: null,
          competentRigger: null,
          onsiteCompetentPersons: [],
          qualifiedEngineer: null,
          autoPopulatedFrom: 'manual',
          populatedAt: at,
        },
    stakeholders: isRecord(v1.stakeholders) ? (v1.stakeholders as PlanDataV2['stakeholders']) : {},
    jhaReference: typeof v1.jhaReference === 'string' ? v1.jhaReference : null,
    liftDetails,
    aiPlan,
    equipmentLists,
    complianceSteps: Array.isArray(v1.complianceSteps)
      ? (v1.complianceSteps as PlanDataV2['complianceSteps'])
      : [],
    attachments: Array.isArray(v1.attachments) ? (v1.attachments as PlanDataV2['attachments']) : [],
    signOff: isRecord(v1.signOff) ? (v1.signOff as PlanDataV2['signOff']) : null,
    livingDocument: isRecord(v1.livingDocument)
      ? (v1.livingDocument as PlanDataV2['livingDocument'])
      : { edits: [] },
    migratedAt: at,
  } as PlanDataV2;
}

export interface PlanRowLike {
  planData: unknown;
  createdAt?: string | null;
}

/** Read path: any stored planData → PlanDataV2 VIEW (v2 passes through untouched). */
export function readPlan(row: PlanRowLike): PlanDataV2 {
  const raw = row.planData;
  let data: Record<string, unknown> = {};
  if (isRecord(raw)) {
    data = raw;
  } else if (typeof raw === 'string') {
    try {
      const parsed: unknown = JSON.parse(raw);
      if (isRecord(parsed)) data = parsed;
    } catch {
      data = {};
    }
  }
  if (data.schemaVersion === 2) {
    return data as unknown as PlanDataV2;
  }
  return upgradeV1toV2(data, typeof row.createdAt === 'string' ? row.createdAt : undefined);
}

/**
 * Write path: build the planData payload for planApi create/update. Stamps
 * schemaVersion/planType, keeps v1 form keys verbatim (server codec upgrades
 * on read), ensures livingDocument exists.
 */
export function writePlan(form: Record<string, unknown>): Record<string, unknown> {
  const payload: Record<string, unknown> = { ...form };
  if (payload.schemaVersion === undefined) {
    // Audit #1: a form carrying serialized v2 liftDetails (the real editing
    // surface) is a v2 payload — stamp the write contract so readPlan passes
    // it through verbatim instead of re-deriving lifts from v1 liftSystems.
    if (Array.isArray(payload.liftDetails) && payload.liftDetails.length > 0) {
      payload.schemaVersion = 2;
      payload.planType = payload.planType ?? 'construction';
      payload.livingDocument = isRecord(payload.livingDocument) ? payload.livingDocument : { edits: [] };
      return payload;
    }
    // Form payloads are v1-shaped; keep keys verbatim for the server codec.
    // Only stamp what the write contract requires.
    payload.livingDocument = isRecord(payload.livingDocument) ? payload.livingDocument : { edits: [] };
    return payload;
  }
  payload.schemaVersion = 2;
  payload.planType = payload.planType ?? 'construction';
  payload.livingDocument = isRecord(payload.livingDocument) ? payload.livingDocument : { edits: [] };
  return payload;
}

/** True when a stored payload is still v1 (show the "Upgrade to v2" button, F-2). */
export function isV1Plan(row: PlanRowLike): boolean {
  const raw = row.planData;
  return !(isRecord(raw) && raw.schemaVersion === 2);
}
